import { SupporterList } from '@/types';
import { AllListCallStatistics, ListSummary } from '@/types/shared';
import { DomainCode } from '@australiangreens/ag-internal-components';
import { logger } from '@/util';
import callBackendApi from '@/apis/call';
import { mapListSortColumnForApi, serializeListFilterForApi } from '@/apis/listGridFilters';
import type {
  GetListAddressCountParameters,
  GetListParameters,
  ListRecord,
} from './types';

export const getListMetadata = async ({
  token,
  listId,
}: Pick<GetListParameters, 'token' | 'listId'>) => {
  const endpoint = `/lists/${listId}/metadata`;
  const data: ListSummary | undefined = await callBackendApi({
    token,
    method: 'get',
    endpoint,
  });
  return data;
};

export const getListStatistics = async ({
  assignedToSub,
  token,
  listId,
}: Pick<GetListParameters, 'token' | 'listId' | 'assignedToSub' | 'domainCode'>): Promise<
  AllListCallStatistics | undefined
> => {
  const endpoint = `/lists/${listId}/statistics?assigneeId=${encodeURIComponent(
    assignedToSub ?? ''
  )}`;

  return callBackendApi({
    token,
    method: 'get',
    endpoint,
  });
};

export const getList = async ({
  token,
  listId,
  domainCode,
  limit,
  offset,
  assignedToSub,
  filters,
  sortColumn,
  sortOrder,
}: GetListParameters) => {
  logger.debug('getList() called', {
    listId,
    domainCode,
    limit,
    offset,
    assignedToSub,
    filters,
    sortColumn,
    sortOrder,
  });
  let endpoint = `/lists/${listId}?limit=${limit}&offset=${offset}&domainCode=${domainCode}`;

  if (filters && filters.length > 0) {
    endpoint = `${endpoint}&filter=${encodeURIComponent(
      `[${filters.map((filter) => serializeListFilterForApi(filter)).join(',')}]`
    )}`;
  }

  if (sortColumn) {
    endpoint = `${endpoint}&sortColumn=${encodeURIComponent(mapListSortColumnForApi(sortColumn))}`;
  }
  if (sortOrder) {
    endpoint = `${endpoint}&sortOrder=${sortOrder}`;
  }

  if (assignedToSub) {
    endpoint = `${endpoint}&assignedToSub=${encodeURIComponent(assignedToSub)}`;
  }

  const data: SupporterList | undefined = await callBackendApi({
    token,
    method: 'get',
    endpoint,
  });
  return data;
};

export const getListAddressCount = async ({
  token,
  listId,
  domainCode,
  sortColumn,
  sortOrder,
  contactIds,
}: GetListAddressCountParameters) => {
  const contactIdsQuery =
    contactIds && contactIds.length > 0
      ? `&contactIds=${encodeURIComponent(JSON.stringify(contactIds))}`
      : '';

  const endpoint = `/lists/${listId}/addresscount?domainCode=${domainCode}&sortOrder=${sortOrder}&sortColumn=${sortColumn}${contactIdsQuery}`;
  const data = (await callBackendApi({
    token,
    method: 'get',
    endpoint,
  })) as { count: number };
  return data.count;
};

export const getLists = async (
  token: string,
  domainCode: DomainCode,
  createdBySub: string,
  assignedToSub = '',
  coordinatorSub = ''
) => {
  logger.debug(
    `getLists() called: createdbySub=${createdBySub}, assignedToSub=${assignedToSub}, coordinatorSub=${coordinatorSub}`,
    {}
  );

  const queryArgs = [`domainCode=${domainCode}`];

  if (createdBySub !== '') queryArgs.push(`createdBySub=${createdBySub}`);

  if (assignedToSub !== '') queryArgs.push(`assignedToSub=${assignedToSub}`);

  if (coordinatorSub !== '') queryArgs.push(`coordinatorSub=${coordinatorSub}`);

  const endpoint = '/lists?'.concat(queryArgs.join('&'));

  const data = await callBackendApi({ token, method: 'get', endpoint });

  return data as ListSummary[];
};

export const getListsContaining = async ({
  token,
  containedString,
  abortSignal,
}: {
  token: string;
  containedString: string;
  abortSignal?: AbortSignal;
}) => {
  logger.debug('getListsContaining() called');

  const data: ListRecord[] = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/lists/containing/${encodeURIComponent(containedString)}`,
    abortSignal,
  });

  return data;
};

export const getListsByIds = async (token: string, ids: number[]): Promise<ListRecord[]> => {
  logger.debug(`getListsByIds() called: ids=${ids}`, {});

  const data = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/lists?ids=${encodeURIComponent(JSON.stringify(ids))}`,
  });

  return data;
};
